import {
  Barcode,
  BarcodeGenerationType,
  BarcodeStatus,
} from '@buncha/driver/types/printScanBarcode';
import React, {useCallback, useMemo, useState} from 'react';
import {
  generateDummyBarcodeList,
  useChangeToPreprintedFlow,
  useGenerateBarcodesDataForOrder,
  usePrintScanBarcodesContext,
  useSetDeliveryLocationStatusForDriver,
} from '@buncha/driver/hooks/printScanBarcodes';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import BarcodeScanReminderSheet from '../barcodeScanReminderSheet';
import {Box} from '@buncha/components/core/view';
import {CustomText} from '@buncha/components/core/text';
import {DeliveryLocationStatus} from '@buncha/driver/types/common';
import {DriverScreens} from '@buncha/driver/navigation/type';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {MainScreens} from '@buncha/navigation/types';
import {Modules} from '@buncha/utils/types';
import {OrderInfo} from '@buncha/config/graphQL/type';
import {PButton} from '@buncha/components/core/Button';
import {SpinnerComponent} from '@buncha/components/core/loader';
import TitleSubtitleBottomSheet from '@buncha/common/components/TitleSubtitleBottomSheet';
import {Touchable} from '@buncha/components/core/touchable';
import {navigation} from '@buncha/config/navigation';
import {styles} from './style';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

interface PrintBarcodesCTAProps {
  barcodesGenerated: boolean;
  orderIds: number[];
  numberOfBarcodes: number;
  barcodesType: BarcodeGenerationType;
  hasBagSubmittedByAll: boolean;
  b2BOrderIds: number[];
  orderInfo: OrderInfo[];
}

const PrintBarcodesCTA: React.FC<PrintBarcodesCTAProps> = props => {
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const route = useRoute();
  const params: any = route?.params;
  const runId = Number(params?.runId ?? 0);
  const {generateBarcodesDataForOrder, printingLabel} =
    useGenerateBarcodesDataForOrder();
  const {changeToPreprintedFlow, loading} = useChangeToPreprintedFlow();
  const {setDeliveryLocationStatusForDriver, statusLoading} =
    useSetDeliveryLocationStatusForDriver();
  const {state, dispatch} = usePrintScanBarcodesContext();
  const storeBarcodeList = state?.storeBarcodeList ?? [];
  const barcodesPrinting = state?.barcodesPrinting;
  const [openSheet, setOpenSheet] = useState(false);
  const [reminderSheetOpen, setReminderSheetOpen] = useState(false);
  const {title, body, button} =
    useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const toggleReminderSheet = useCallback(() => {
    setReminderSheetOpen(!reminderSheetOpen);
  }, [reminderSheetOpen]);
  const toggleSheet = useCallback(() => {
    setOpenSheet(!openSheet);
  }, [openSheet]);

  const barcodesList = state.barcodesList;
  const totalBarcodes = barcodesList.length;
  const scannedBarcodes = useMemo(() => {
    const scanned = barcodesList.filter(
      (barcode: Barcode) => barcode.status === BarcodeStatus.scanned,
    );
    return scanned.length;
  }, [barcodesList]);

  const allBarcodesScanned =
    scannedBarcodes === totalBarcodes && totalBarcodes !== 0;

  const navigateToScannerScreen = useCallback(() => {
    const ids =
      props?.orderIds.length > 0 ? props?.orderIds : props?.b2BOrderIds;
    return navigation.navigate(MainScreens.Driver, {
      screen: DriverScreens.ScanBarcode,
      params: {
        orderIds: ids,
        totalScanned: barcodesList?.length ?? 0,
        scanned: scannedBarcodes ?? 0,
        barcodeType: state?.barcodeType,
        barcodeList: barcodesList,
        runId: runId,
        orderInfo: props?.orderInfo,
      },
    });
  }, [
    barcodesList,
    props?.b2BOrderIds,
    props?.orderIds,
    props?.orderInfo,
    runId,
    scannedBarcodes,
    state?.barcodeType,
  ]);

  const navigateToVanLoad = useCallback(() => {
    return navigation.navigate(MainScreens.Driver, {
      screen: DriverScreens.LoadVanAt,
      params: {runId: runId},
    });
  }, [runId]);

  const changeStatusAndNavigate = useCallback(async () => {
    if (runId) {
      const ids =
        props?.orderIds.length > 0 ? props?.orderIds : props?.b2BOrderIds;
      await setDeliveryLocationStatusForDriver(
        runId,
        DeliveryLocationStatus.driverAssigned,
        ids,
        navigateToVanLoad,
      );
    }
  }, [
    navigateToVanLoad,
    props?.b2BOrderIds,
    props?.orderIds,
    runId,
    setDeliveryLocationStatusForDriver,
  ]);

  const getCtaText = useMemo(() => {
    if (printingLabel) return button?.printing;
    else if (props?.barcodesGenerated && !allBarcodesScanned)
      return button?.startScanning;
    else if (allBarcodesScanned) return button?.continue;
    return button?.printBarcodes;
  }, [
    allBarcodesScanned,
    button?.continue,
    button?.printBarcodes,
    button?.printing,
    button?.startScanning,
    printingLabel,
    props?.barcodesGenerated,
  ]);

  const generateBarcodesAndPrint = useCallback(
    async (printBarcodes: boolean, callBack?: Function) => {
      await generateBarcodesDataForOrder(
        props?.orderIds || [],
        printBarcodes,
        callBack,
        runId,
        props?.b2BOrderIds || [],
      );
    },
    [generateBarcodesDataForOrder, props?.b2BOrderIds, props?.orderIds, runId],
  );

  const onCTAPress = useCallback(async () => {
    const ids =
      props?.orderIds.length > 0 ? props?.orderIds : props?.b2BOrderIds;
    if (!props?.barcodesGenerated && ids.length)
      return await generateBarcodesAndPrint(true);
    else if (allBarcodesScanned) return changeStatusAndNavigate();
    return toggleReminderSheet();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allBarcodesScanned, props?.barcodesGenerated, props?.orderIds.length]);

  const createDummyBarcodes = useCallback(async () => {
    const ids =
      props?.orderIds.length > 0 ? props?.orderIds : props?.b2BOrderIds;
    if (barcodesList.length > 0) {
      const success = await changeToPreprintedFlow(ids, runId);
      if (success) {
        generateDummyBarcodeList(
          props?.numberOfBarcodes,
          dispatch,
          barcodesList,
        );
        toggleSheet();
      }
    } else {
      await generateBarcodesAndPrint(false, async (barcodes: Barcode[]) => {
        const success = await changeToPreprintedFlow(ids, runId);
        if (success) {
          generateDummyBarcodeList(props?.numberOfBarcodes, dispatch, barcodes);
          toggleSheet();
        }
      });
    }
  }, [
    barcodesList,
    changeToPreprintedFlow,
    dispatch,
    generateBarcodesAndPrint,
    props?.b2BOrderIds,
    props?.numberOfBarcodes,
    props?.orderIds,
    runId,
    toggleSheet,
  ]);

  return (
    <React.Fragment>
      <Box style={[styles.ctaBox, {borderColor: theme?.text?.gray?.[200]}]}>
        <PButton
          style={styles.cta}
          isLoading={barcodesPrinting || statusLoading || printingLabel}
          title={getCtaText}
          loadingIndicator={<SpinnerComponent />}
          onPress={onCTAPress}
          disabled={!props?.hasBagSubmittedByAll}
        />
        <If
          condition={
            props?.barcodesType === BarcodeGenerationType.system &&
            !allBarcodesScanned
          }>
          <Touchable
            style={styles.printerIssues}
            onPress={toggleSheet}
            disabled={!props?.hasBagSubmittedByAll}>
            <CustomText>
              <CustomText style={{fontFamily: muli?.[600]?.normal}}>
                {body?.printerIssues}
              </CustomText>
              <CustomText
                style={[
                  styles.printerIssuesText,
                  {fontFamily: muli?.[600]?.normal},
                ]}>
                {body?.useBackupStickers}
              </CustomText>
            </CustomText>
          </Touchable>
        </If>
      </Box>

      <TitleSubtitleBottomSheet
        title={title?.printerMalfunction}
        subtitle={body?.printerMalfunction}
        ctaText={button?.useBackupStickers}
        onCTAPress={createDummyBarcodes}
        open={openSheet}
        onClose={toggleSheet}
        loading={loading}
      />
      <BarcodeScanReminderSheet
        onCTAPress={navigateToScannerScreen}
        open={reminderSheetOpen}
        onClose={toggleReminderSheet}
        storeBarcodeList={storeBarcodeList}
      />
    </React.Fragment>
  );
};

export default React.memo(PrintBarcodesCTA);
